import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { awsConfig, cloudFeaturesEnabled } from "../config";
import { AuthContext } from "./auth-context";

function createManager(): UserManager | null {
  if (!cloudFeaturesEnabled) return null;
  return new UserManager({
    authority: `https://cognito-idp.${awsConfig.region}.amazonaws.com/${awsConfig.userPoolId}`,
    client_id: awsConfig.clientId,
    redirect_uri: `${window.location.origin}/auth/callback`,
    response_type: "code",
    scope: "openid email profile",
    automaticSilentRenew: true,
    monitorSession: false,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  });
}

export function AuthProvider({ children }: PropsWithChildren) {
  const manager = useMemo(() => createManager(), []);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(Boolean(manager));

  useEffect(() => {
    if (!manager) return;
    let mounted = true;
    const initialize = async () => {
      try {
        if (window.location.pathname === "/auth/callback") {
          const callbackUser = await manager.signinRedirectCallback();
          if (mounted) setUser(callbackUser);
          window.history.replaceState({}, "", "/");
          window.dispatchEvent(new PopStateEvent("popstate"));
        } else {
          const storedUser = await manager.getUser();
          if (mounted) setUser(storedUser && !storedUser.expired ? storedUser : null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void initialize();
    const onLoaded = (loaded: User) => setUser(loaded);
    const onUnloaded = () => setUser(null);
    manager.events.addUserLoaded(onLoaded);
    manager.events.addUserUnloaded(onUnloaded);
    return () => {
      mounted = false;
      manager.events.removeUserLoaded(onLoaded);
      manager.events.removeUserUnloaded(onUnloaded);
    };
  }, [manager]);

  const signIn = useCallback(async () => {
    if (!manager) return;
    await manager.signinRedirect();
  }, [manager]);

  const signOut = useCallback(async () => {
    if (!manager) return;
    await manager.removeUser();
    const domain = awsConfig.cognitoDomain.replace(/\/$/, "");
    const logout = new URL(`${domain}/logout`);
    logout.searchParams.set("client_id", awsConfig.clientId);
    logout.searchParams.set("logout_uri", window.location.origin);
    window.location.assign(logout.toString());
  }, [manager]);

  const value = useMemo(
    () => ({
      user,
      loading,
      enabled: cloudFeaturesEnabled,
      signIn,
      signOut,
    }),
    [user, loading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
